import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle, GoogleLogo, MicrosoftOutlookLogo, IdentificationBadge, Calendar, User } from "@phosphor-icons/react";

const STEPS = [
  { id: "profile", label: "Your info" },
  { id: "calendly", label: "Booking link" },
  { id: "calendar", label: "Calendar sync" },
  { id: "review", label: "Review" },
];

export default function Setup() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.profile?.name || user?.name || "",
    title: user?.profile?.title || "",
    company: user?.profile?.company || "",
    phone: user?.profile?.phone || "",
    website: user?.profile?.website || "",
    linkedin: user?.profile?.linkedin || "",
    tagline: user?.profile?.tagline || "",
    bio: user?.profile?.bio || "",
    calendly_url: user?.profile?.calendly_url || "",
    booking_cta: user?.profile?.booking_cta || "Book a meeting with me",
  });

  const saveProfile = async () => {
    setBusy(true);
    try { await api.put("/auth/profile", profile); await refreshUser(); }
    catch { toast.error("Save failed"); throw new Error("save"); }
    finally { setBusy(false); }
  };

  const next = async () => {
    if (step === 0 || step === 1) {
      try { await saveProfile(); } catch { return; }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async () => {
    setBusy(true);
    try {
      await saveProfile();
      await api.put("/auth/onboarding", { onboarding_complete: true });
      await refreshUser();
      toast.success("You're all set!");
      nav("/dashboard");
    } catch { toast.error("Something went wrong"); }
    finally { setBusy(false); }
  };

  const publicUrl = `${window.location.origin}/c/${user?.slug}`;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="setup-page">
      <Toaster theme="dark" />

      <header className="border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[900px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/dashboard" className="font-display font-black text-2xl tracking-tighter">
            MY NEXUS<span style={{ color: "var(--accent)" }}> </span>CARD
          </Link>
          <button onClick={finish} disabled={busy} data-testid="setup-skip" className="overline hover:text-white">skip for now →</button>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-[900px] mx-auto px-6 pt-8">
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex-1 flex items-center" data-testid={`step-${i}`}>
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 flex items-center justify-center border-2 font-mono text-sm font-bold"
                  style={{
                    borderColor: i <= step ? "var(--accent)" : "var(--border)",
                    background: i < step ? "var(--accent)" : "transparent",
                    color: i < step ? "var(--bg)" : (i === step ? "var(--accent)" : "var(--text-3)"),
                  }}>
                  {i < step ? "✓" : i + 1}
                </div>
                <div className="overline mt-2 whitespace-nowrap" style={i <= step ? { color: "var(--text)" } : {}}>{s.label}</div>
              </div>
              {i < STEPS.length - 1 && (
                <div className="flex-1 h-[2px] mx-2 md:mx-4" style={{ background: i < step ? "var(--accent)" : "var(--border)" }}></div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-[900px] mx-auto px-6 py-12">
        {/* STEP 0 — Profile */}
        {step === 0 && (
          <section data-testid="step-profile">
            <div className="flex items-center gap-3 mb-4">
              <User size={28} weight="duotone" color="var(--accent)" />
              <div className="overline" style={{ color: "var(--accent)" }}>// step 01 · your info</div>
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3">Tell us who you are.</h1>
            <p className="mb-8" style={{ color: "var(--text-2)" }}>This powers your digital business card and every message template.</p>

            <div className="border p-6 grid grid-cols-1 md:grid-cols-2 gap-4" style={{ borderColor: "var(--border)" }}>
              {[
                ["name", "Full name *", true], ["title", "Title", false], ["company", "Company", false],
                ["phone", "Phone", false], ["website", "Website", false], ["linkedin", "LinkedIn URL", false],
              ].map(([k, l, req]) => (
                <div key={k}>
                  <label className="label-base">{l}</label>
                  <input required={!!req} value={profile[k]} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} className="input-base" data-testid={`setup-${k}`} />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="label-base">Tagline (one-liner people remember you by)</label>
                <input value={profile.tagline} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })} placeholder="e.g. Helping small business owners save 30% on insurance" className="input-base" data-testid="setup-tagline" />
              </div>
            </div>
          </section>
        )}

        {/* STEP 1 — Calendly */}
        {step === 1 && (
          <section data-testid="step-calendly">
            <div className="flex items-center gap-3 mb-4">
              <IdentificationBadge size={28} weight="duotone" color="var(--accent)" />
              <div className="overline" style={{ color: "var(--accent)" }}>// step 02 · booking link</div>
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3">Paste your Calendly link.</h1>
            <p className="mb-8" style={{ color: "var(--text-2)" }}>
              Every contact who scans your digital card sees a "Book a meeting" button. It opens your Calendly — pre-configured, always up-to-date, zero effort from you after this one step.
            </p>

            <div className="border p-6" style={{ borderColor: "var(--border)" }}>
              <label className="label-base">Your Calendly link</label>
              <input value={profile.calendly_url} onChange={(e) => setProfile({ ...profile, calendly_url: e.target.value })}
                placeholder="https://calendly.com/yourname/30min" className="input-base" data-testid="setup-calendly-url" />
              <p className="text-xs mt-2" style={{ color: "var(--text-3)" }}>Don't have Calendly yet? <a href="https://calendly.com/signup" target="_blank" rel="noreferrer" className="underline hover:text-white">Create a free account →</a> (takes 2 min)</p>

              <label className="label-base mt-6">Button text (what shows on your card)</label>
              <input value={profile.booking_cta} onChange={(e) => setProfile({ ...profile, booking_cta: e.target.value })}
                placeholder="Book a meeting with me" className="input-base" data-testid="setup-booking-cta" />
            </div>

            {profile.calendly_url && (
              <div className="mt-6 border p-4 flex items-center justify-between" style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.06)" }}>
                <div>
                  <div className="overline mb-1" style={{ color: "var(--accent)" }}>// preview</div>
                  <div className="font-mono text-sm break-all">{profile.calendly_url}</div>
                </div>
                <a href={profile.calendly_url} target="_blank" rel="noreferrer" data-testid="preview-calendly" className="btn-secondary flex items-center gap-2 whitespace-nowrap">
                  Test link <ArrowRight weight="bold" />
                </a>
              </div>
            )}
          </section>
        )}

        {/* STEP 2 — Calendar sync */}
        {step === 2 && (
          <section data-testid="step-calendar">
            <div className="flex items-center gap-3 mb-4">
              <Calendar size={28} weight="duotone" color="var(--accent)" />
              <div className="overline" style={{ color: "var(--accent)" }}>// step 03 · calendar sync</div>
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3">Sync your calendar.</h1>
            <p className="mb-8" style={{ color: "var(--text-2)" }}>
              Calendly connects directly to Google, Outlook, iCloud, and Office 365. This is a <strong style={{ color: "var(--text)" }}>one-time setup inside Calendly</strong> — click below to open the right settings page. Once connected, Calendly checks your real availability every time someone tries to book. No double-bookings.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
              <a href="https://calendly.com/integrations/google_calendar" target="_blank" rel="noreferrer" data-testid="connect-google"
                className="border-r border-b p-6 flex items-start gap-4 hover:bg-[#15303A] transition group" style={{ borderColor: "var(--border)" }}>
                <GoogleLogo size={32} weight="duotone" color="var(--accent)" />
                <div className="flex-1">
                  <div className="font-display font-bold text-lg">Google Calendar</div>
                  <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Sync availability + auto-add booked meetings to your Google Calendar.</p>
                  <div className="overline mt-3 flex items-center gap-2" style={{ color: "var(--accent)" }}>Connect via Calendly <ArrowRight weight="bold" /></div>
                </div>
              </a>
              <a href="https://calendly.com/integrations/outlook" target="_blank" rel="noreferrer" data-testid="connect-outlook"
                className="border-r border-b p-6 flex items-start gap-4 hover:bg-[#15303A] transition group" style={{ borderColor: "var(--border)" }}>
                <MicrosoftOutlookLogo size={32} weight="duotone" color="var(--accent)" />
                <div className="flex-1">
                  <div className="font-display font-bold text-lg">Outlook / Office 365</div>
                  <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Two-way sync with Outlook, Exchange, or Office 365.</p>
                  <div className="overline mt-3 flex items-center gap-2" style={{ color: "var(--accent)" }}>Connect via Calendly <ArrowRight weight="bold" /></div>
                </div>
              </a>
              <a href="https://calendly.com/integrations/icloud" target="_blank" rel="noreferrer" data-testid="connect-icloud"
                className="border-r border-b p-6 flex items-start gap-4 hover:bg-[#15303A] transition" style={{ borderColor: "var(--border)" }}>
                <Calendar size={32} weight="duotone" color="var(--accent)" />
                <div className="flex-1">
                  <div className="font-display font-bold text-lg">iCloud Calendar</div>
                  <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Apple / iCloud users — sync via Calendly's iCloud connector.</p>
                  <div className="overline mt-3 flex items-center gap-2" style={{ color: "var(--accent)" }}>Connect via Calendly <ArrowRight weight="bold" /></div>
                </div>
              </a>
              <div className="border-r border-b p-6 flex items-start gap-4" style={{ borderColor: "var(--border)", background: "rgba(45,212,212,0.04)" }}>
                <CheckCircle size={32} weight="duotone" color="var(--accent)" />
                <div>
                  <div className="font-display font-bold text-lg">Already connected?</div>
                  <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>If your Calendly is already synced with your calendar, you're done. Click Continue.</p>
                </div>
              </div>
            </div>

            <p className="text-xs mt-6" style={{ color: "var(--text-3)" }}>
              Note: My Nexus Card doesn't store or read your calendar data — Calendly handles all calendar syncing on their end. We just deep-link you to the right setting.
            </p>
          </section>
        )}

        {/* STEP 3 — Review */}
        {step === 3 && (
          <section data-testid="step-review">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle size={28} weight="duotone" color="var(--accent)" />
              <div className="overline" style={{ color: "var(--accent)" }}>// step 04 · review &amp; launch</div>
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3">Ready to go live.</h1>
            <p className="mb-8" style={{ color: "var(--text-2)" }}>Here's what people will see when you share your card.</p>

            <div className="border p-8 grid-bg" style={{ borderColor: "var(--accent)" }}>
              <div className="overline mb-4">// {profile.company || "independent operator"}</div>
              <div className="font-display font-black text-4xl md:text-5xl tracking-tighter">{profile.name || "Your name"}</div>
              {profile.title && <div className="font-mono text-sm mt-3 uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{profile.title}</div>}
              {profile.tagline && <div className="mt-4">{profile.tagline}</div>}
              <div className="mt-6 grid grid-cols-2 gap-3 text-sm font-mono">
                {user?.email && <div><div className="overline">email</div><div>{user.email}</div></div>}
                {profile.phone && <div><div className="overline">phone</div><div>{profile.phone}</div></div>}
              </div>
              {profile.calendly_url && (
                <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--accent)" }}>
                  <div className="overline mb-2" style={{ color: "var(--accent)" }}>// booking button preview</div>
                  <div className="btn-primary inline-flex items-center gap-2 pointer-events-none">
                    {profile.booking_cta || "Book a meeting with me"} <ArrowRight weight="bold" />
                  </div>
                </div>
              )}
              <div className="mt-6 pt-6 border-t flex items-center justify-between text-xs" style={{ borderColor: "var(--border)" }}>
                <span className="overline">your public URL</span>
                <span className="font-mono truncate">{publicUrl}</span>
              </div>
            </div>
          </section>
        )}

        {/* Nav buttons */}
        <div className="mt-10 flex items-center justify-between">
          <button onClick={back} disabled={step === 0} data-testid="setup-back"
            className="btn-secondary flex items-center gap-2" style={step === 0 ? { opacity: 0.3, cursor: "not-allowed" } : {}}>
            <ArrowLeft weight="bold" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} disabled={busy} data-testid="setup-next" className="btn-primary flex items-center gap-2">
              {busy ? "Saving..." : "Continue"} <ArrowRight weight="bold" />
            </button>
          ) : (
            <button onClick={finish} disabled={busy} data-testid="setup-finish" className="btn-primary flex items-center gap-2">
              {busy ? "Finalizing..." : "Launch my card"} <ArrowRight weight="bold" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
